import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { articleCategories, articlesData } from '@/mocks/articlesData';
import { getWebSiteSchema, getWebPageSchema, getBreadcrumbSchema } from '@/utils/schemas';

const articleImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Fresh%20green%20pears%20in%20a%20wooden%20basket%20on%20a%20rustic%20wooden%20table%2C%20soft%20natural%20window%20light%2C%20warm%20cozy%20farmhouse%20atmosphere%2C%20summer%20harvest%20aesthetic%2C%20editorial%20food%20photography%2C%20earthy%20green%20tones%2C%20organic%20composition&width=700&height=460&seq=article-cuiguan-2026&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=A%20golden%20trophy%20and%20certificate%20placed%20next%20to%20beautiful%20golden%20pears%20on%20a%20elegant%20marble%20surface%2C%20soft%20natural%20lighting%2C%20prestigious%20award%20ceremony%20aesthetic%2C%20warm%20golden%20tones%2C%20editorial%20photography&width=700&height=460&seq=article-award-2026&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Cut%20pear%20slices%20arranged%20artistically%20on%20a%20white%20plate%20with%20nutritional%20herbs%20and%20honey%2C%20soft%20natural%20lighting%2C%20healthy%20food%20aesthetic%2C%20clean%20minimal%20composition%2C%20editorial%20food%20photography&width=700&height=460&seq=article-nutrition&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Steamed%20pear%20dessert%20in%20a%20beautiful%20ceramic%20bowl%20with%20rock%20sugar%20and%20goji%20berries%2C%20warm%20cozy%20lighting%2C%20traditional%20Chinese%20dessert%20aesthetic%2C%20steam%20rising%2C%20food%20photography&width=700&height=460&seq=article-recipe&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Autumn%20scene%20with%20ripe%20pears%20on%20a%20wooden%20table%20surrounded%20by%20fall%20leaves%2C%20warm%20golden%20hour%20light%2C%20cozy%20seasonal%20aesthetic%2C%20harvest%20theme%2C%20editorial%20photography%2C%20rustic%20charm&width=700&height=460&seq=article-autumn-health&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Modern%20smart%20orchard%20with%20drone%20flying%20over%20pear%20trees%2C%20digital%20technology%20in%20agriculture%2C%20warm%20sunset%20light%2C%20innovative%20farming%20aesthetic%2C%20professional%20photography&width=700&height=460&seq=article-tech-2026&orientation=landscape',
  'article-7': 'https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20hand%20selecting%20a%20perfect%20ripe%20pear%20from%20a%20wooden%20crate%20at%20a%20farmers%20market%2C%20natural%20outdoor%20lighting%2C%20fresh%20produce%20aesthetic%2C%20editorial%20photography&width=700&height=460&seq=article-pick-pear&orientation=landscape',
  'article-8': 'https://readdy.ai/api/search-image?query=Elegant%20pear%20salad%20and%20baked%20pear%20dessert%20on%20a%20beautiful%20wooden%20table%2C%20soft%20natural%20lighting%2C%20gourmet%20food%20photography%2C%20artistic%20plating%2C%20warm%20inviting%20aesthetic&width=700&height=460&seq=article-creative-recipe&orientation=landscape',
};

export default function ArticlesPage() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredArticles = activeCategory === 'all'
    ? articlesData
    : articlesData.filter((a) => a.category === activeCategory);

  const pageTitle = '梨果资讯 - 惠美梨产品动态、梨果知识与美食食谱 | 惠美梨';
  const pageDesc = '惠美梨梨果资讯频道，为您提供最新产品动态、专业梨果知识、创意美食食谱与健康养生资讯。了解惠美梨品牌动态，学习梨果挑选与食用技巧。';

  const schemas = [
    getWebSiteSchema(),
    getWebPageSchema('惠美梨梨果资讯', pageDesc, 'https://huimeili.com/articles'),
    getBreadcrumbSchema([
      { name: '首页', url: 'https://huimeili.com/' },
      { name: '梨果资讯', url: 'https://huimeili.com/articles' },
    ]),
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        canonicalUrl="https://huimeili.com/articles"
        schema={schemas}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">梨果资讯</span>
            </nav>

            <div className="text-center mb-10 md:mb-14">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
                梨果资讯
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                了解惠美梨最新动态、梨果知识、美食食谱与健康养生资讯
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {articleCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/articles/${article.id}`}
                  className="group rounded-2xl bg-background-100 overflow-hidden cursor-pointer hover:bg-background-100 transition-colors"
                >
                  <div className="relative h-48 md:h-52 overflow-hidden">
                    <img
                      src={articleImages[article.id] || articleImages['article-1']}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-800 text-xs font-medium">
                        {articleCategories.find(c => c.id === article.category)?.name || article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                      <span>{article.date}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{article.readTime}</span>
                    </div>
                    <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}